"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import { permissionForPath } from "@/lib/permissions";
import { api, type NowPlaying } from "@/lib/api";
import { AlbumArt } from "@/components/dashboard/album-art";
import { StationSwitcher } from "@/components/dashboard/station-switcher";
import { useStation } from "@/lib/station-context";

interface NavItem {
  href: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

// Icons kept as inline SVG (Lucide-style paths), matching the approved
// mockup exactly rather than pulling in the full lucide-react icon set
// for a fixed, known list that never changes.
const icons = {
  home: (
    <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1Z" />
  ),
  // A music note: two round heads (real circles, not zero-length arcs, which draw lopsided).
  media: (
    <>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </>
  ),
  playlist: <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />,
  stations: (
    <path d="M4 11a8 8 0 0 1 16 0v7a2 2 0 0 1-2 2h-1v-7h-3v7H6a2 2 0 0 1-2-2ZM12 11a2 2 0 1 0 0 .01" />
  ),
  schedule: <path d="M3 5h18M3 10h18M8 3v4M16 3v4M4 5a2 2 0 0 0-1 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-1-2" />,
  live: <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3ZM19 10v1a7 7 0 0 1-14 0v-1M12 18v4M8 22h8" />,
  // Headphones: band plus two ear cups.
  listen: (
    <path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3" />
  ),
  requests: (
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
  ),
  statistics: <path d="M3 3v18h18M18 17V9M13 17V5M8 17v-4" />,
  monitoring: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />,
  settings: (
    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 0 1-4 0v-.09A1.7 1.7 0 0 0 9 19.37a1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.63 15a1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 0 1 0-4h.09A1.7 1.7 0 0 0 4.63 9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.63a1.7 1.7 0 0 0 1.03-1.56V3a2 2 0 0 1 4 0v.09A1.7 1.7 0 0 0 15 4.63a1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.37 9a1.7 1.7 0 0 0 1.56 1.03H21a2 2 0 0 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1.97Z" />
  ),
  logs: <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6M8 13h8M8 17h6" />,
  website: <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />,
  users: (
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  ),
};

const navItems: NavItem[] = [
  { href: "/", label: "Home", icon: icons.home },
  { href: "/media", label: "Media", icon: icons.media },
  { href: "/playlists", label: "Playlists", icon: icons.playlist },
  { href: "/stations", label: "Stations", icon: icons.stations },
  { href: "/schedule", label: "Schedule", icon: icons.schedule },
  { href: "/live", label: "Live DJs", icon: icons.live },
  { href: "/listen", label: "Listen", icon: icons.listen },
  { href: "/requests", label: "Requests", icon: icons.requests },
  { href: "/statistics", label: "Statistics", icon: icons.statistics },
  { href: "/monitoring", label: "Monitoring", icon: icons.monitoring },
  { href: "/logs", label: "Logs", icon: icons.logs },
  { href: "/settings", label: "Settings", icon: icons.settings },
  { href: "/website", label: "Website", icon: icons.website },
  { href: "/users", label: "Users", icon: icons.users },
];

const COLLAPSED_KEY = "sidebar-collapsed";

function readCollapsed(): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false; // storage blocked: just start expanded
  }
}

export function Sidebar({ nowPlaying: reported }: { nowPlaying: NowPlaying | null }) {
  // Icons-only mode, remembered per browser. The sidebar only mounts after the
  // account has loaded in the browser, so reading storage here can't mismatch
  // the server render.
  const [collapsed, setCollapsed] = useState(readCollapsed);
  function toggleCollapsed() {
    setCollapsed((c) => {
      const next = !c;
      try {
        window.localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        // Not remembered, still works for this visit.
      }
      return next;
    });
  }
  // Only show a track while the station is actually on air; otherwise the
  // last-played track would linger after the station goes OFFLINE.
  const nowPlaying = reported?.on_air ? reported : null;
  const pathname = usePathname();
  const { can, user } = useAuth();
  const stationId = useStation().station?.id ?? null;
  // The red badge on Requests: how many are waiting for a decision.
  const canSeeRequests = can("requests.view");
  const [pendingRequests, setPendingRequests] = useState(0);
  const badgeFor = (item: NavItem) => (item.href === "/requests" ? pendingRequests : (item.badge ?? 0));
  useEffect(() => {
    if (!canSeeRequests) return;
    let cancelled = false;
    async function load() {
      try {
        const s = await api.requestsSummary();
        if (!cancelled) setPendingRequests(s.pending);
      } catch {
        // Leave the last number; the badge is a convenience.
      }
    }
    const first = setTimeout(load, 0);
    const poll = setInterval(load, 30000);
    window.addEventListener("requests-changed", load);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(poll);
      window.removeEventListener("requests-changed", load);
    };
  }, [canSeeRequests, stationId]);
  // Only the pages this account may open (Home and the open ones always show).
  const visibleItems = navItems.filter((item) => {
    const required = permissionForPath(item.href);
    return !required || can(required);
  });

  return (
    <div
      className={cn(
        "relative z-30 flex shrink-0 flex-col border-r border-sidebar-border bg-sidebar-bg transition-[width] duration-200",
        collapsed ? "w-[72px]" : "w-[250px]"
      )}
    >
      <button
        type="button"
        onClick={toggleCollapsed}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-expanded={!collapsed}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="absolute -right-3 top-[30px] z-40 flex h-6 w-6 items-center justify-center rounded-full border border-sidebar-border bg-sidebar-bg text-sidebar-text shadow hover:bg-accent-blue hover:text-white"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={cn("h-3.5 w-3.5 transition-transform", collapsed && "rotate-180")}>
          <path d="m15 18-6-6 6-6" />
        </svg>
      </button>
      <div className={cn("flex items-center gap-3 border-b border-sidebar-border py-[22px]", collapsed ? "justify-center px-0" : "px-5")}>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-700">
          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" className="h-5 w-5">
            <path d="M4 11a8 8 0 0 1 16 0" />
            <path d="M7 13.5a4.5 4.5 0 0 1 9 0" />
            <circle cx="11.5" cy="17.5" r="1.5" fill="#fff" stroke="none" />
          </svg>
        </div>
        {!collapsed && (
          <div>
            <div className="text-[17px] font-bold leading-tight text-white">ROA Radio</div>
            <div className="mt-px text-xs text-sidebar-text">Music. Always.</div>
          </div>
        )}
      </div>

      <StationSwitcher collapsed={collapsed} />

      <nav className={cn("flex flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden p-3", collapsed && "px-2")}>
        {visibleItems.map((item) => {
          const isActive = pathname === item.href;
          return (
            <Link
              key={item.href}
              href={item.href}
              title={collapsed ? item.label : undefined}
              aria-label={collapsed ? item.label : undefined}
              className={cn(
                "relative flex items-center gap-3 rounded-[10px] py-2.5 text-sm font-medium text-sidebar-text hover:bg-white/5 hover:text-slate-200",
                collapsed ? "justify-center px-0" : "px-3.5",
                isActive && "bg-accent-blue text-white hover:bg-accent-blue hover:text-white"
              )}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[19px] w-[19px] shrink-0">
                {item.icon}
              </svg>
              {!collapsed && item.label}
              {badgeFor(item) > 0 &&
                (collapsed ? (
                  <span className="absolute right-2 top-1.5 h-2.5 w-2.5 rounded-full bg-accent-red ring-2 ring-sidebar-bg" />
                ) : (
                  <span className="ml-auto flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent-red px-1 text-[11px] font-bold text-white">
                    {badgeFor(item) > 99 ? "99+" : badgeFor(item)}
                  </span>
                ))}
            </Link>
          );
        })}
      </nav>

      {/* The manual is for the built-in Admin role only (the API enforces it too). */}
      {user?.role === "admin" && (
        <div className={cn("px-3 pb-2", collapsed && "px-2")}>
          <Link
            href="/manual"
            title={collapsed ? "Documentation" : undefined}
            aria-label={collapsed ? "Documentation" : undefined}
            className={cn(
              "flex items-center gap-3 rounded-[10px] py-2.5 text-sm font-medium text-sidebar-text hover:bg-white/5 hover:text-slate-200",
              collapsed ? "justify-center px-0" : "px-3.5",
              pathname === "/manual" && "bg-accent-blue text-white hover:bg-accent-blue hover:text-white"
            )}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[19px] w-[19px] shrink-0">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            {!collapsed && "Documentation"}
          </Link>
        </div>
      )}

      <div className={cn("flex flex-col gap-2.5 border-t border-sidebar-border", collapsed ? "items-center p-3" : "p-4")}>
        <div
          className={cn("flex items-center gap-2.5 rounded-xl bg-white/[0.04]", collapsed ? "p-1.5" : "px-3 py-2.5")}
          title={collapsed ? `${nowPlaying?.title || "Nothing playing"}${nowPlaying?.artist ? ` — ${nowPlaying.artist}` : ""}` : undefined}
        >
          {nowPlaying?.track_id && nowPlaying.has_cover_art ? (
            // size="md" already matches this box's 34px dimensions — see
            // album-art.tsx's default; rounded-lg matches the sidebar's
            // other rounded tiles (AlbumArt's own default is rounded-md).
            <AlbumArt trackId={nowPlaying.track_id} hasCoverArt className="rounded-lg" />
          ) : (
            // No embedded art on the current track (or nothing playing
            // yet) — the station's own mark, not a broken image icon.
            <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-accent-blue">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                <path d="M4 15v-6M8 18v-12M12 20v-16M16 18v-12M20 15v-6" />
              </svg>
            </div>
          )}
          <div className={cn("min-w-0", collapsed && "hidden")}>
            <div className="text-[10px] uppercase tracking-wide text-sidebar-text">Now Playing</div>
            <div className="truncate text-[13px] font-semibold text-white">
              {nowPlaying?.title || "Nothing playing"}
            </div>
            <div className="truncate text-[11px] text-sidebar-text">{nowPlaying?.artist || (reported ? "Offline" : "—")}</div>
          </div>
        </div>
        {collapsed ? (
          <div className="text-[10px] font-semibold text-slate-400">v1.0</div>
        ) : (
          <div className="px-0.5 text-[11px] leading-relaxed text-sidebar-text">
            <span className="font-semibold text-slate-300">ROA Radio v1.0.0</span>
            <br />© 2026 ROA Technologies Inc. All rights reserved.
          </div>
        )}
      </div>
    </div>
  );
}
